import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import { useAuth } from "@cayuse/shared-react.utils.auth";

import CovertButton from "@cayuse/shared-react.components.covert-button";
import Icon from "@cayuse/shared-react.components.icon";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import SPLink from "components/SPLink";
import Table from "components/Table";
import { AWARDS_BASE_ROUTE, PROPOSALS_BASE_ROUTE, MAX_TASKS_SHOWED } from "globals/constants";
import { useDictionary, useComponentDidMount } from "hooks";
import { getTasks } from "store/actions/userTasks";
import { userTaskListSelectors } from "store/selectors";
import { filterAllUserTasks } from "utils";

import styles from "./styles.css";


const TIMEOUT_IN_SECONDS = 300;

const TasksDrawer = () => {
	const [ isOpen, setIsOpen ] = useState(false);
	const didMount = useComponentDidMount();
	const { userTasks } = useSelector(userTaskListSelectors.content);
	const [ tasks, setTasks ]  = useState([]);
	const dictionary = useDictionary("tasksDrawer");
	const { isAuthenticated } = useAuth();

	const hasTasks = isNonEmptyArray(tasks);
	const numReviewTasks = hasTasks ? tasks.length : 0;

	const numTasksDisplay = useMemo(() => {
		return numReviewTasks < MAX_TASKS_SHOWED
			? numReviewTasks
			: dictionary.overTaskLimitLabel || "15+";
	}, [ dictionary.overTaskLimitLabel, numReviewTasks ]);

	// todo: this will come from user settings
	const isMuted = false;
	const showTaskCount = hasTasks && !isMuted;

	useEffect(() => {
		if (isNonEmptyArray(userTasks)) {
			const reviewTasks = filterAllUserTasks(userTasks);
			if (userTasks.length > MAX_TASKS_SHOWED) {
				reviewTasks.pop();
			}
			setTasks(reviewTasks);
		} else {
			setTasks([]);
		}
	}, [ userTasks ]);

	useEffect(() => {
		if (isAuthenticated) {
			if (!didMount) {
				getTasks();
			} else {
				const interval = setInterval(() => {
					getTasks();
				}, (TIMEOUT_IN_SECONDS * 1000));

				return () => {
					clearInterval(interval);
				};
			}
		}
	}, [ didMount, isAuthenticated ]);

	const close = useCallback(() => {
		setIsOpen(false);
		document.querySelector("dialog.task-drawer-dialog").close();
	}, []);

	const toggleOpen = useCallback(() => {
		setIsOpen(prev => !prev);

		if (document.querySelector("aside.task-drawer-wrapper").getAttribute("aria-expanded") === "true") {
			document.querySelector("dialog.task-drawer-dialog").close();
		} else {
			document.querySelector("dialog.task-drawer-dialog").showModal();
		}
	}, []);

	const ariaControlsAttribute = isOpen ? { [`aria-controls`]: "tasks-drawer-aside"  } : { };

	return (
		<dialog className="task-drawer-dialog">
			<aside className={`${styles.tasksDrawer} ${isOpen ? styles.open : ""} task-drawer-wrapper`} aria-expanded={isOpen}>
				<div className={styles.buttonWrapper}>
					<button aria-expanded={isOpen} aria-haspopup="dialog" {...ariaControlsAttribute} className={styles.openButton} onClick={toggleOpen}>
						<div className={styles.spacer}>
							<div className={styles.label}>
								<span>{dictionary.openButtonLabel || "My Tasks"}</span>
								<div className={`${styles.counter} ${showTaskCount ? styles.active : ""}`}>
									{numTasksDisplay}
								</div>
							</div>
						</div>
					</button>
				</div>

				<div id="tasks-drawer-aside" className={`${isOpen ? "" : styles.drawerClosed}`} aria-hidden={!isOpen}>
					<CovertButton
						aria-label={`Close ${dictionary.openButtonLabel || "My Tasks"}`}
						className={styles.closeButton}
						disabled={!isOpen}
						onClick={close}
					>
						<Icon name="times" size="bigger" />
					</CovertButton>

					<div className={styles.body}>
						<div className={styles.tasksWrapper}>
							<div className={styles.tableWrapper}>
								<div className={styles.tableScrollWrapper}>
									<Table className={styles.taskTable} noContentBorders header={dictionary.openButtonLabel || "My Tasks"} captionText="My Tasks">
										<Table.Header>
											<Table.Row>
												<Table.HeaderCell>{dictionary.tableColumns[0]}</Table.HeaderCell>
												<Table.HeaderCell>{dictionary.tableColumns[1]}</Table.HeaderCell>
												<Table.HeaderCell>{dictionary.tableColumns[2]}</Table.HeaderCell>
											</Table.Row>
										</Table.Header>
										<Table.Body>
											{hasTasks
												? tasks.map((task, index) => {
													const isAward = task.submissionType === "award";
													const url = `${isAward ? AWARDS_BASE_ROUTE : PROPOSALS_BASE_ROUTE}/${task.submissionId}`;

													return (
														<Table.Row key={`task-${index}`}>
															<Table.Cell>
																<SPLink to={url} onClick={close}>{task.taskCounter}</SPLink>
															</Table.Cell>
															<Table.Cell>
																{task.name}
															</Table.Cell>
															<Table.Cell>
																{task.submissionNumber}
															</Table.Cell>
														</Table.Row>
													);
												})
												: (
													<Table.Row>
														<Table.Cell className={styles.noTasks} colSpan="3">
															{dictionary.noTasks || "No outstanding tasks"}
														</Table.Cell>
													</Table.Row>
												)
											}
										</Table.Body>
									</Table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</aside>
		</dialog>
	);
};

export default TasksDrawer;
